import React, { useState } from 'react';
import {
  FormFieldType,
  type ConformityValue,
  type FormAnswer,
  type FormField as FormFieldSchema,
} from '@troia-v3/types';

import {
  Checkbox,
  FileDropzone,
  FormField,
  Input,
  RadioGroup,
  Select,
  Textarea,
} from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { ConformityField } from '@/modules/checklists/components/ConformityField';
import { PhotoCaptureField, uploadChecklistFile } from '@/modules/checklists/components/PhotoCaptureField';

/**
 * Renderer de campo de formulário/checklist — extraído de
 * `modules/checklists/pages/ChecklistFillPage.tsx` em 2026-08-21 (captura por
 * QR) SEM mudança de interface. Consumido por: `ChecklistFillPage`,
 * `FormFillModal` e a etapa "Formulário" da modal de captura.
 *
 * Os controles compostos (`ConformityField`, `PhotoCaptureField`) continuam em
 * `modules/checklists/components` — são específicos de checklist (foto com
 * câmera, conformidade) e o shared só os reexporta pelo renderer.
 */
export type AnswerValue = FormAnswer['value'];

/** Campos de layout — não geram resposta nem entram na validação. */
export const LAYOUT_TYPES: ReadonlySet<FormFieldType> = new Set([FormFieldType.HEADING, FormFieldType.PARAGRAPH]);

export const isEmptyAnswer = (value: AnswerValue | undefined): boolean =>
  value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);

export const asStringArray = (value: AnswerValue | undefined): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

export interface FillFieldProps {
  field: FormFieldSchema;
  value: AnswerValue | undefined;
  error?: string;
  onChange: (value: AnswerValue) => void;
  /** Repassa a contagem de uploads em voo do `PhotoCaptureField` para a página. */
  onPendingChange: (count: number) => void;
  /**
   * Omite o `<label>` do `FormField` — para quando o título da pergunta já é
   * renderizado pelo caller (ex.: etapa "Formulário" da captura QR, que usa um
   * `<h3>` próprio). Default `false` — zero mudança para `FormFillModal`/
   * `ChecklistFillPage`, que continuam com o label do `FormField`.
   */
  hideLabel?: boolean;
}

/** Tipos que caem no `Input` de texto — só muda o `type`/`inputMode`. */
const TEXT_INPUT_TYPES: Partial<Record<FormFieldType, { type: string; inputMode?: 'text' | 'email' | 'tel' | 'url' | 'numeric' }>> = {
  [FormFieldType.SHORT_TEXT]: { type: 'text' },
  [FormFieldType.EMAIL]: { type: 'email', inputMode: 'email' },
  [FormFieldType.PHONE]: { type: 'tel', inputMode: 'tel' },
  [FormFieldType.URL]: { type: 'url', inputMode: 'url' },
  [FormFieldType.CPF]: { type: 'text', inputMode: 'numeric' },
  [FormFieldType.CNPJ]: { type: 'text', inputMode: 'numeric' },
  [FormFieldType.DATE]: { type: 'date' },
  [FormFieldType.TIME]: { type: 'time' },
};

/**
 * Mapeamento tipo → controle. A referência de QUAIS tipos existem e como se
 * comportam é o renderer público (`automation/forms/public/FormPublicPage.tsx`),
 * mas aqui tudo é reimplementado sobre os componentes shared: aquela página é
 * pública/white-label (inputs nativos estilizados com a cor do formulário),
 * enquanto esta vive dentro do app e precisa dos primitivos do DS
 * (CLAUDE.md NUNCA #9/#32).
 */
export const FillField: React.FC<FillFieldProps> = ({ field, value, error, onChange, onPendingChange, hideLabel = false }) => {
  if (field.type === FormFieldType.HEADING) {
    return (
      <h2 className="text-base font-bold pt-4 pb-2 first:pt-0" style={{ color: 'var(--theme-card-text)' }}>
        {field.label}
      </h2>
    );
  }

  if (field.type === FormFieldType.PARAGRAPH) {
    return (
      <p className="text-[13px] pb-3" style={{ color: 'var(--theme-muted-text)' }}>
        {field.label}
      </p>
    );
  }

  const textInput = TEXT_INPUT_TYPES[field.type];

  const control = ((): React.ReactNode => {
    if (field.type === FormFieldType.CONFORMITY) {
      return (
        <ConformityField
          fieldId={field.id}
          value={typeof value === 'string' ? value : null}
          onChange={(next: ConformityValue) => onChange(next)}
        />
      );
    }

    if (field.type === FormFieldType.PHOTO) {
      return (
        <PhotoCaptureField
          fieldId={field.id}
          {...(field.photoConfig ? { config: field.photoConfig } : {})}
          value={asStringArray(value)}
          onChange={onChange}
          onPendingChange={onPendingChange}
        />
      );
    }

    if (textInput) {
      return (
        <Input
          type={textInput.type}
          {...(textInput.inputMode ? { inputMode: textInput.inputMode } : {})}
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => onChange(event.target.value)}
          {...(field.placeholder ? { placeholder: field.placeholder } : {})}
          {...(error ? { error } : {})}
          data-testid={`checklist-input-${field.id}`}
        />
      );
    }

    if (field.type === FormFieldType.NUMBER) {
      return (
        <Input
          type="number"
          inputMode="numeric"
          value={typeof value === 'number' ? String(value) : ''}
          onChange={(event) => onChange(event.target.value === '' ? null : Number(event.target.value))}
          {...(field.placeholder ? { placeholder: field.placeholder } : {})}
          {...(error ? { error } : {})}
          data-testid={`checklist-input-${field.id}`}
        />
      );
    }

    if (field.type === FormFieldType.LONG_TEXT) {
      return (
        <Textarea
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => onChange(event.target.value)}
          rows={4}
          {...(field.placeholder ? { placeholder: field.placeholder } : {})}
          {...(error ? { error } : {})}
          data-testid={`checklist-input-${field.id}`}
        />
      );
    }

    if (field.type === FormFieldType.SELECT) {
      return (
        <Select
          value={typeof value === 'string' ? value : ''}
          onChange={onChange}
          options={(field.options ?? []).map((option) => ({ value: option.value, label: option.label }))}
          placeholder="Selecione…"
          {...(error ? { error } : {})}
          data-testid={`checklist-input-${field.id}`}
        />
      );
    }

    if (field.type === FormFieldType.RADIO) {
      // Primitivo shared com a variante `touch` (alvo ≥48px) — mantém o
      // `<input type="radio">` sr-only, e com ele a semântica de radiogroup e a
      // navegação por setas do teclado.
      return (
        <RadioGroup
          variant="cards"
          size="touch"
          options={(field.options ?? []).map((option) => ({ value: option.value, label: option.label }))}
          value={typeof value === 'string' ? value : ''}
          onChange={onChange}
          data-testid={`checklist-option-${field.id}`}
        />
      );
    }

    if (field.type === FormFieldType.CHECKBOX || field.type === FormFieldType.MULTI_SELECT) {
      const selectedValues = asStringArray(value);
      return (
        <div className="flex flex-col gap-2.5" data-testid={`checklist-input-${field.id}`}>
          {(field.options ?? []).map((option) => (
            <Checkbox
              key={option.id}
              label={option.label}
              size="touch"
              checked={selectedValues.includes(option.value)}
              onChange={(checked) =>
                onChange(
                  checked
                    ? [...selectedValues, option.value]
                    : selectedValues.filter((item) => item !== option.value),
                )
              }
              data-testid={`checklist-option-${field.id}-${option.value}`}
            />
          ))}
        </div>
      );
    }

    if (field.type === FormFieldType.FILE_UPLOAD) {
      return <ChecklistFileField fieldId={field.id} value={typeof value === 'string' ? value : ''} onChange={onChange} />;
    }

    if (field.type === FormFieldType.RATING) {
      const rating = typeof value === 'number' ? value : 0;
      return (
        <div className="flex items-center gap-1" data-testid={`checklist-input-${field.id}`}>
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => onChange(star)}
              className="flex items-center justify-center rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring-color)]"
              style={{ width: 48, height: 48 }}
              aria-label={`${star} de 5`}
              data-testid={`checklist-rating-${field.id}-${star}`}
            >
              <Icon
                name="star"
                className="w-7 h-7"
                style={{ color: rating >= star ? 'var(--theme-warning-color)' : 'var(--theme-input-border)' }}
              />
            </button>
          ))}
        </div>
      );
    }

    if (field.type === FormFieldType.LINEAR_SCALE) {
      return (
        <div className="flex flex-wrap gap-1.5" data-testid={`checklist-input-${field.id}`}>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((step) => {
            const selected = value === step;
            return (
              <button
                key={step}
                type="button"
                onClick={() => onChange(step)}
                aria-pressed={selected}
                className="rounded-lg text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring-color)]"
                style={{
                  minWidth: 44,
                  height: 44,
                  border: `1.5px solid ${selected ? 'var(--theme-primary)' : 'var(--theme-input-border)'}`,
                  backgroundColor: selected ? 'var(--theme-primary)' : 'var(--theme-input-background)',
                  color: selected ? '#ffffff' : 'var(--theme-text)',
                }}
                data-testid={`checklist-scale-${field.id}-${step}`}
              >
                {step}
              </button>
            );
          })}
        </div>
      );
    }

    return null;
  })();

  // O `Input`/`Select`/`Textarea` shared já pintam a borda e a mensagem de erro;
  // para os controles compostos (conformidade, foto, escalas) o erro sai aqui.
  const showErrorInWrapper =
    !!error && !textInput && field.type !== FormFieldType.NUMBER && field.type !== FormFieldType.LONG_TEXT &&
    field.type !== FormFieldType.SELECT;

  return (
    <div
      id={`checklist-field-${field.id}`}
      className="py-3 border-t first:border-t-0 first:pt-0"
      style={{ borderColor: 'var(--theme-card-border)' }}
      data-testid={`checklist-field-${field.id}`}
    >
      {hideLabel ? (
        // Sem o wrapper `FormField` (que sempre renderiza `<label>`) — o caller já
        // mostra o título da pergunta por conta própria. Mantém description/erro
        // no mesmo espaçamento do `FormField` (`space-y-1`) para não descasar.
        <div className="space-y-1">
          {field.description && (
            <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>{field.description}</p>
          )}
          {control}
          {showErrorInWrapper && (
            <p className="text-sm" style={{ color: 'var(--theme-error-color)' }} role="alert">{error}</p>
          )}
        </div>
      ) : (
        <FormField
          label={field.label}
          required={field.required}
          {...(field.description ? { description: field.description } : {})}
          {...(showErrorInWrapper ? { error } : {})}
        >
          {control}
        </FormField>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* file_upload — mesmo endpoint das fotos, valor = URL                 */
/* ------------------------------------------------------------------ */

const ChecklistFileField: React.FC<{ fieldId: string; value: string; onChange: (value: AnswerValue) => void }> = ({
  fieldId,
  value,
  onChange,
}) => {
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files: File[]): Promise<void> => {
    const file = files[0];
    if (!file) return;
    setUploading(true);
    try {
      onChange(await uploadChecklistFile(file));
    } catch {
      // Interceptor global de `@/lib/api` já notificou — o dropzone volta ao
      // estado ocioso e o usuário tenta de novo.
    } finally {
      setUploading(false);
    }
  };

  if (value) {
    return (
      <div
        className="flex items-center gap-2 rounded-lg px-3 py-2"
        style={{ border: '1px solid var(--theme-card-border)' }}
        data-testid={`checklist-file-${fieldId}`}
      >
        <Icon name="file" className="w-4 h-4 flex-none" style={{ color: 'var(--theme-muted-text)' }} />
        <a
          href={value}
          target="_blank"
          rel="noreferrer"
          className="text-sm truncate flex-1 hover:underline"
          style={{ color: 'var(--theme-primary)' }}
        >
          Arquivo enviado
        </a>
        <button
          type="button"
          onClick={() => onChange(null)}
          className="p-1.5 rounded-lg flex-none"
          style={{ color: 'var(--theme-muted-text)' }}
          aria-label="Remover arquivo"
          data-testid={`checklist-file-${fieldId}-remove`}
        >
          <Icon name="x" className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <FileDropzone
      onFiles={(files) => void handleFiles(files)}
      uploading={uploading}
      label="Toque para escolher ou arraste um arquivo"
      showButton
      data-testid={`checklist-file-${fieldId}`}
    />
  );
};
